import { useCallback, useState } from "react";
import { Box, useTheme } from "@mui/material";

import type { UUID } from "@thalamos/aspire-common";

import { api } from "../../../api.js";
import {
  Banner,
  BannerList,
  Button,
  PopupDialog,
  PopupDialogTitle,
  renderErrorToast,
  renderSuccessToast,
  TextField,
} from "~/components/design-system/index.js";

/** API functions that can be injected for testing */
export interface CancelDeMinimisDialogApi {
  cancelDeMinimis: (
    formId: UUID,
    body: { fileNoteId: number; reason: string },
  ) => Promise<{ status: number }>;
}

/** Default API implementation using the real API */
const defaultApi: CancelDeMinimisDialogApi = {
  cancelDeMinimis: (formId, body) => api.forms.cancelDeMinimis(formId, body),
};

export function CancelDeMinimisDialog({
  formId,
  fileNoteId,
  onClose,
  onSuccess,
  apiOverride,
}: {
  formId: UUID;
  fileNoteId: number;
  onClose: () => void;
  onSuccess: () => void;
  /** Optional API override for testing */
  apiOverride?: CancelDeMinimisDialogApi;
}) {
  const apiClient = apiOverride ?? defaultApi;
  const theme = useTheme();
  const [reason, setReason] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleConfirm = useCallback(async () => {
    if (!reason.trim()) return;
    setIsSubmitting(true);
    try {
      const result = await apiClient.cancelDeMinimis(formId, {
        fileNoteId,
        reason: reason.trim(),
      });
      if (result.status === 204) {
        renderSuccessToast({
          message: "De minimis correction request cancelled",
        });
        onSuccess();
      }
    } catch (e: unknown) {
      renderErrorToast({
        message:
          e instanceof Error
            ? e.message
            : "Failed to cancel de minimis correction",
      });
      setIsSubmitting(false);
    }
  }, [formId, fileNoteId, reason, onSuccess, apiClient]);

  return (
    <PopupDialog open={true} onClose={onClose} maxWidth="sm">
      <PopupDialogTitle
        titleText="Cancel De Minimis Correction"
        closeDialog={onClose}
      />
      <Box sx={{ mb: 2 }}>
        <Banner
          body={[
            "Are you sure you want to cancel this de minimis correction request? The signatory will be notified.",
          ]}
          bannerType={BannerList.WARNING}
        />
      </Box>

      <TextField
        name="reason"
        label="Reason for cancellation"
        onChange={(e: string) => {
          setReason(e);
        }}
        value={reason}
        useFullWidth={true}
        multiline
        rows="4"
      />

      <Box
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: "space-between",
          marginTop: theme.spacing(3),
        }}
      >
        <Button
          label="Back"
          onClick={onClose}
          variant="outlined"
          disabled={isSubmitting}
        />
        <Button
          color="error"
          disabled={isSubmitting || !reason.trim()}
          label="Cancel Request"
          onClick={() => {
            void handleConfirm();
          }}
        />
      </Box>
    </PopupDialog>
  );
}
